import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/accordion'
import { Box, Flex } from '@chakra-ui/layout'
import {
  Alert,
  AlertDescription,
  Link,
  Select,
  Switch,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { GrSettingsOption } from 'react-icons/gr'
import { colors } from '../../../../ui-kit'
import { SettingsToggleBox } from '..'
import type { SettingsResponse } from '../../../interfaces'
import PermissionGuard from '../../PermissionGuard'
import { PermissionsEnum } from '../../../../utils'

const ImageSettingsAdvanced = ({
  values,
  setFieldValue,
  handleChange,
}: FormikProps<SettingsResponse>): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <PermissionGuard
      permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
    >
      {
        (hasPermission: boolean) => (
          <AccordionItem>
            <AccordionButton px={12} py={8}>
              <Flex
                flex="1"
                textAlign="left"
                lineHeight="1.5em"
                fontWeight="700"
                style={{ gap: '1em' }}
              >
                <GrSettingsOption fontSize="1.5em" />
                {t('advanced')}
              </Flex>
              <Box
                background={colors.appPaleGreyThree}
                borderRadius="50%"
                padding="0.25em"
              >
                <AccordionIcon color={colors.appSlate} display="block" />
              </Box>
            </AccordionButton>

            <AccordionPanel px={12} py={8} position="relative">
              <VStack spacing="0.75em">
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t(
                        'advanced_autorotate',
                        'Use correct rotation from image metadata',
                      )}
                    </ChakraText>
                    <Switch
                      name="advanced.autoRotate"
                      isDisabled={!hasPermission}
                      isChecked={values.advanced.autoRotate === 'true'}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked.toString())
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t('advanced_autorotate_description')}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/image-optimization/metadata-color-profile-and-orientation#image-orientation"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      ON
                    </ChakraText>
                  </ChakraText>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t(
                        'chroma_subsampling_enabled',
                        'Use 4:2:0 Chroma Subsampling',
                      )}
                    </ChakraText>
                    <Switch
                      name="advanced.useChromaSubsampling"
                      isDisabled={!hasPermission}
                      isChecked={values.advanced.useChromaSubsampling === 'true'}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked.toString())
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t('chroma_subsampling_description')}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/image-optimization/chroma-subsampling#chroma-subsampling"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      ON
                    </ChakraText>
                    {t(
                      'chroma_subsampling_recommendation',
                      ', but only after testing for quality of images on your website',
                    )}
                  </ChakraText>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('save_copy_images', 'Save a copy of original images')}
                    </ChakraText>
                    <Switch
                      name="advanced.uploadOriginalImages"
                      isChecked={values.advanced.uploadOriginalImages === 'true'}
                      isDisabled={!hasPermission || true}
                    />
                  </Flex>
                  <ChakraText>{t('save_copy_images_description')}</ChakraText>
                  <Alert status="info">
                    <AlertDescription fontSize="sm">
                      {t(
                        'save_copy_images_plan_warning',
                        'This feature is available on enterprise plan. To change this setting ',
                      )}{' '}
                      <Link isExternal fontSize="sm" href="/dashboard/support">
                        {t(
                          'save_copy_images_plan_warning_contact_support',
                          'contact support',
                        )}
                      </Link>
                    </AlertDescription>
                  </Alert>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t(
                        'auto_convert_svg_enabled',
                        'Auto convert SVG input images to raster formats',
                      )}
                    </ChakraText>
                    <Switch
                      name="advanced.autoConvertSvgs"
                      isDisabled={!hasPermission}
                      isChecked={values.advanced.autoConvertSvgs === true}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked)
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t(
                      'auto_convert_svg_description',
                      'When enabled, SVG images will be automatically converted to raster formats like PNG or JPEG if it results in a smaller file size. This is done only when automatic format optimization is enabled via either the account settings or the "f-auto" transformation string in the URL.',
                    )}
                  </ChakraText>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t(
                        'dpr_last_step_only',
                        'Apply DPR in final transformation step only',
                      )}
                    </ChakraText>
                    <Switch
                      name="advanced.dprLastStepOnly"
                      isDisabled={!hasPermission}
                      isChecked={values.advanced.dprLastStepOnly === true}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked)
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t(
                      'dpr_last_step_only_description',
                      'When enabled, the Device Pixel Ratio (DPR) multiplication factor is only applied in the final step of chained transformations. By default, DPR is multiplied into dimension parameters at every step in the chain, which can cause incorrect intermediate sizes. Enable this to ensure DPR scaling only affects the final output dimensions.',
                    )}
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      ON
                    </ChakraText>
                    {t(
                      'dpr_last_step_only_recommendation',
                      ' for new integrations. Test existing URLs before enabling account-wide.',
                    )}
                  </ChakraText>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('preserve_image_metadata')}
                    </ChakraText>
                  </Flex>
                  <ChakraText>
                    {t('preserve_image_metadata_description')}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/image-optimization/metadata-color-profile-and-orientation#image-metadata"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      {t('metadata_discard_all_metdata')}
                    </ChakraText>
                    {t('preserve_image_metadata_description_recommendation')}'
                    {t('metadata_preserve_only_color_profile')}
                    '.
                  </ChakraText>
                  <Select
                    name="advanced.metadata"
                    isDisabled={!hasPermission}
                    maxWidth="250px"
                    value={values.advanced.metadata}
                    onChange={handleChange}
                  >
                    <option value="none">{t('metadata_discard_all_metdata')}</option>
                    <option value="only_color">
                      {t('metadata_preserve_only_color_profile')}
                    </option>
                    <option value="full">{t('metadata_preserve_complete')}</option>
                  </Select>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('png_compression_mode', 'PNG Image Compression mode')}
                    </ChakraText>
                  </Flex>
                  <ChakraText>{t('png_compression_mode_description')}</ChakraText>
                  <ChakraText>{t('png_compression_mode_recommendation')}</ChakraText>
                  <Select
                    name="advanced.pngCompressionMode"
                    isDisabled={!hasPermission}
                    maxWidth="250px"
                    value={values.advanced.pngCompressionMode}
                    onChange={handleChange}
                  >
                    <option value="none">{t('none', 'None')}</option>
                    <option value="min">{t('minimum', 'Minimum')}</option>
                    <option value="max">{t('maximum', 'Maximum')}</option>
                  </Select>
                </SettingsToggleBox>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )
      }
    </PermissionGuard>
  )
}

export default ImageSettingsAdvanced
